import { NavLink } from '@/components/NavLink';
import { Link } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import {
  ChevronDown,
  ChevronUp,
  Building2,
  Database,
  Users,
  Loader2,
  Bluetooth,
  Usb,
  Wifi,
  Printer,
  Trash2,
  ScanBarcode,
  Tablet,
  MonitorPlay,
  Monitor,
  TouchpadIcon,
} from 'lucide-react';
import { useState, useRef, useCallback } from 'react';
import { cn } from '@/lib/utils';
import posVisionLogo from '@/assets/posvision-logo.png';
import posVisionIcon from '@/assets/posvision-logo.png';
import { useImpersonate } from '@/contexts/ImpersonateContext';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { CatalogApi } from '@/api/catalog';
import { toast } from 'sonner';
import { useConfirm } from '@/components/ConfirmProvider';
import { useQueryClient } from '@tanstack/react-query';
import { useFeatureToggles } from '@/hooks/useFeatureToggles';
import { useTenantModules, type ModuleKey } from '@/hooks/useTenantModules';
import { useMyPermissions } from '@/hooks/usePermissions';
import { useUniverseContext } from '@/hooks/useUniverses';
import { bypassKioskThisSession } from '@/hooks/useKioskMode';
import {
  menuItems,
  DEMO_SECTORS,
  DEMO_CUSTOMERS,
  SUBSECTION_MODULE_MAP,
  type MenuItem,
  type SubItem,
} from '@/components/sidebar/sidebarConfig';
import { filterMenuByPermissions } from '@/components/sidebar/permissionFilter';
import { NetworkPrinterDialog } from '@/components/sidebar/NetworkPrinterDialog';
import { ConfigPinDialog } from '@/components/sidebar/ConfigPinDialog';
import { CertificationCodeDialog } from '@/components/sidebar/CertificationCodeDialog';
import { SidebarChildrenSection } from '@/components/sidebar/SidebarChildrenSection';
import { SidebarFooter, TouchStockShortcut } from '@/components/sidebar/TouchMenuParts';
import { useOpenCurrentSection } from '@/components/sidebar/useOpenCurrentSection';
import { useAutoUpdate } from '@/hooks/useAutoUpdate';
import { isCertificationUnlocked } from '@/lib/certification-lock';
import { Lock, Unlock } from 'lucide-react';
import { errOf } from '@/api/result';
import { appErrorMessage } from '@/api/errors';

/** touch : tiroir d'un PDA ou d'un téléphone, une seule rubrique ouverte. */
export default function AppSidebar({ touch = false }: { touch?: boolean }) {
  const confirm = useConfirm();
  const [collapsed, setCollapsed] = useState(false);
  const [openSections, setOpenSections] = useState<string[]>(touch ? [] : ['Fichiers', 'Magasin']);
  const [openSubSections, setOpenSubSections] = useState<string[]>([]);
  const [demoOpen, setDemoOpen] = useState(false);
  const [deploying, setDeploying] = useState<string | null>(null);
  const [showNetworkPrinter, setShowNetworkPrinter] = useState(false);

  // PIN gate for Configuration section — session-scoped unlock.
  // Long-press (800ms) on Configuration → PIN dialog.
  // Super_admin / impersonation bypass automatically.
  const [configUnlocked, setConfigUnlocked] = useState(false);
  const [showPinDialog, setShowPinDialog] = useState(false);
  const [certUnlocked, setCertUnlocked] = useState(isCertificationUnlocked);
  const [showCertDialog, setShowCertDialog] = useState(false);
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressFiredRef = useRef(false);
  const location = useLocation();
  const { isImpersonating, impersonatedName, impersonatedUserId } = useImpersonate();
  const { userRole, user } = useAuth();
  const queryClient = useQueryClient();
  const { checkNow, checking: updateChecking } = useAutoUpdate();
  const { universes, activeUniverse, setActiveUniverse } = useUniverseContext();

  // ─── Triple-tap on logo → force update check ───
  const tapCountRef = useRef(0);
  const tapTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleLogoTap = useCallback(() => {
    // Retour explicite au menu classique : suspendre l'auto-redirect kiosque
    // pour la session courante (rétabli au prochain démarrage de l'app).
    bypassKioskThisSession();
    tapCountRef.current += 1;
    if (tapTimerRef.current) clearTimeout(tapTimerRef.current);
    if (tapCountRef.current >= 3) {
      tapCountRef.current = 0;
      toast.info('Vérification des mises à jour…');
      checkNow().then(() => {
        toast.success('Vérification terminée');
      });
      return;
    }
    tapTimerRef.current = setTimeout(() => {
      tapCountRef.current = 0;
    }, 800);
  }, [checkNow]);

  const isSuperAdmin = userRole === 'super_admin';
  const isTenantAdmin = userRole === 'admin' || isSuperAdmin;
  const isLinkVision = user?.email?.endsWith('@linkvision.fr') ?? false;
  const { isEnabled: isModuleEnabled } = useTenantModules();
  const { isFeatureEnabled } = useFeatureToggles();

  // Config section access : unlocked si super_admin, impersonation, ou PIN vérifié cette session
  const bypassPin = isSuperAdmin || isImpersonating;
  const configAccessible = bypassPin || configUnlocked;

  // Long-press handler (800ms) pour ouvrir le dialog PIN sur la section Configuration
  const handleConfigLongPressStart = useCallback(() => {
    longPressFiredRef.current = false;
    longPressTimerRef.current = setTimeout(() => {
      longPressFiredRef.current = true;
      if (!configAccessible) setShowPinDialog(true);
    }, 800);
  }, [configAccessible]);

  const handleConfigLongPressEnd = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  const handleConfigClick = useCallback(
    (title: string) => {
      // Si long-press a déjà ouvert le PIN, ignorer le click simple
      if (longPressFiredRef.current) {
        longPressFiredRef.current = false;
        return;
      }
      if (!configAccessible) {
        setShowPinDialog(true);
        return;
      }
      setOpenSections((prev) =>
        prev.includes(title) ? prev.filter((s) => s !== title) : [...prev, title],
      );
    },
    [configAccessible],
  );

  // Map sidebar section titles → module keys
  const SECTION_MODULE_MAP: Record<string, ModuleKey> = {
    'Caisse rapide': 'mod_caisse_rapide',
    'Ticket W': 'mod_bons_provisoires',
    Fichiers: 'mod_fichiers',
    Magasin: 'mod_magasin',
    // Titre réel du menu = "Gestion fiscal" (ex "Gestion") — les deux clés
    // sont gardées pour éviter une régression si le titre re-change.
    Gestion: 'mod_gestion',
    'Gestion fiscal': 'mod_gestion',
    'Magic SMS': 'mod_magic_sms',
    'Tablette serveur': 'mod_tablette_serveur',
    Stocks: 'mod_stocks',
    Fidélité: 'mod_fidelite',
    Marketing: 'mod_marketing',
    Rapports: 'mod_rapports',
    Finance: 'mod_finance',
    Equipe: 'mod_equipe',
    Website: 'mod_website',
    Configuration: 'mod_configuration',
    Restaurant: 'mod_restaurant_salle',
    Hôtel: 'mod_hotel',
    Bar: 'mod_bar',
    Café: 'mod_bar',
    Togo: 'mod_togo',
  };

  // Filter menu items based on tenant modules
  // super_admin sees everything ONLY when NOT impersonating a client
  // When impersonating, apply the tenant's module filters so the menu matches what the client sees
  const bypassModuleFilter = isSuperAdmin && !isImpersonating;
  // Filtre transverse : retire les items `superAdminOnly` si on n'est pas super_admin hors impersonation,
  // et retire les SubItem `adminOnly` si on n'est ni admin tenant ni super_admin.
  const menuItemsScoped = menuItems
    .filter((item) => !item.superAdminOnly || bypassModuleFilter)
    .map((item) => {
      if (!item.children) return item;
      const filtered = item.children.filter(
        (c) => (!c.adminOnly || isTenantAdmin) && (!c.featureKey || isFeatureEnabled(c.featureKey)),
      );
      return filtered.length === item.children.length ? item : { ...item, children: filtered };
    });
  const moduleFilteredMenuItems = bypassModuleFilter
    ? menuItemsScoped
    : menuItemsScoped
        .filter((item) => {
          // Caisse classique masquable par feature toggle (marchand caisse rapide only)
          if (item.url === '/caisse') return isFeatureEnabled('caisse_classique');
          if (item.url === '/monnayeur') return isFeatureEnabled('monnayeur_automatique');
          if (item.url === '/balance') return isFeatureEnabled('vente_au_poids');
          const moduleKey = SECTION_MODULE_MAP[item.title];
          if (!moduleKey) return true; // Accueil always visible
          return isModuleEnabled(moduleKey);
        })
        .map((item) => {
          // Within Magasin, filter Cuisine (KDS) and Tablette serveur sub-entries
          if (item.title === 'Magasin' && item.children) {
            return {
              ...item,
              children: item.children.filter((child) => {
                if (child.url === '/cuisine') return isModuleEnabled('mod_cuisine_kds');
                if (child.url === '/chr/serveur') return isModuleEnabled('mod_tablette_serveur');
                if (child.url === '/salle-display') return isModuleEnabled('mod_restaurant_salle');
                return true;
              }),
            };
          }
          // Configuration : Togo/Restaurant/Hôtel/Café sont maintenant des sous-sections.
          // On filtre chaque sous-section selon son module key (SUBSECTION_MODULE_MAP) et
          // on applique les règles fines qui étaient précédemment au niveau top-level Restaurant.
          if (item.title === 'Configuration' && item.sections) {
            const filteredSections = item.sections
              .filter((section) => {
                const moduleKey = SUBSECTION_MODULE_MAP[section.title];
                return !moduleKey || isModuleEnabled(moduleKey);
              })
              .map((section) => {
                if (section.title === 'Restaurant') {
                  return {
                    ...section,
                    children: section.children.filter((child) => {
                      if (child.url === '/caisse-restaurant')
                        return (
                          isModuleEnabled('mod_restaurant_salle') ||
                          isModuleEnabled('mod_cuisine_kds')
                        );
                      if (child.url === '/kiosque') return isModuleEnabled('mod_kiosque');
                      if (child.url === '/commandes')
                        return (
                          isModuleEnabled('mod_cuisine_kds') ||
                          isModuleEnabled('mod_kiosque') ||
                          isModuleEnabled('mod_restaurant_salle')
                        );
                      if (child.url === '/chr/serveur')
                        return isModuleEnabled('mod_tablette_serveur');
                      if (child.url === '/cuisine') return isModuleEnabled('mod_cuisine_kds');
                      if (child.url === '/salle-display')
                        return isModuleEnabled('mod_restaurant_salle');
                      return true;
                    }),
                  };
                }
                return section;
              });
            return { ...item, sections: filteredSections };
          }
          return item;
        });

  // Filtre par permissions utilisateur (défauts du rôle + overrides user_permissions).
  // Admins/super_admin voient tout ; tant que le RPC n'a pas répondu, pas de filtrage
  // (évite un menu vide sur échec réseau). Masquage menu uniquement, pas de garde route.
  const { can: canUser, loaded: permissionsLoaded } = useMyPermissions();
  const visibleMenuItems =
    isTenantAdmin || !permissionsLoaded
      ? moduleFilteredMenuItems
      : filterMenuByPermissions(moduleFilteredMenuItems, canUser);

  const deployDemoCatalog = async (sector: string) => {
    if (!impersonatedUserId) return;
    setDeploying(sector);
    try {
      // Get the tenant_id for the impersonated user
      const { data: profile } = await supabase
        .from('profiles')
        .select('tenant_id')
        .eq('user_id', impersonatedUserId)
        .single();

      if (!profile?.tenant_id) throw new Error('Tenant non trouvé');

      const { data, error } = await supabase.functions.invoke('deploy-catalog', {
        body: { sector, tenant_id: profile.tenant_id },
      });

      if (error) throw error;
      toast.success(
        `Catalogue "${sector}" déployé : ${data.categories_created} catégories, ${data.products_created} produits`,
      );
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['categories'] });
    } catch (err: any) {
      toast.error(`Erreur : ${err.message}`);
    } finally {
      setDeploying(null);
    }
  };

  const deployDemoCustomers = async () => {
    if (!impersonatedUserId) return;
    setDeploying('customers');
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('tenant_id')
        .eq('user_id', impersonatedUserId)
        .single();

      if (!profile?.tenant_id) throw new Error('Tenant non trouvé');

      // NON MIGRÉ LOT 5 : bulk insert démo avec tenant_id explicit — ProfilesApi.createCustomer
      // ne supporte pas le bulk ni l'injection manuelle de tenant_id (4.A.10). Laissé intact.
      const rows = DEMO_CUSTOMERS.map((c) => ({ ...c, tenant_id: profile.tenant_id }));
      const { error } = await supabase.from('customers').insert(rows as never);
      if (error) throw error;
      toast.success(`${rows.length} clients de démonstration ajoutés`);
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    } catch (err: any) {
      toast.error(`Erreur : ${err.message}`);
    } finally {
      setDeploying(null);
    }
  };

  const purgeDemoData = async (type: 'products' | 'customers' | 'fournisseurs') => {
    if (!impersonatedUserId) return;
    const label =
      type === 'products'
        ? 'Produits & catégories'
        : type === 'customers'
          ? 'Clients'
          : 'Fournisseurs';
    if (
      !(await confirm({
        title: `Supprimer toutes les données "${label}" de ce compte ?`,
        destructive: true,
      }))
    )
      return;
    setDeploying(`purge-${type}`);
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('tenant_id')
        .eq('user_id', impersonatedUserId)
        .single();
      if (!profile?.tenant_id) throw new Error('Tenant non trouvé');

      if (type === 'products') {
        // Strangler lot 1 : purge catalogue via CatalogApi (invariant 4.B.2)
        const result = await CatalogApi.purgeByTenant(profile.tenant_id);
        if (!result.ok) throw new Error(appErrorMessage(errOf(result)));
        queryClient.invalidateQueries({ queryKey: ['products'] });
        queryClient.invalidateQueries({ queryKey: ['categories'] });
      } else if (type === 'customers') {
        // NON MIGRÉ LOT 5 : delete all par tenant — ProfilesApi n'expose pas de deleteCustomer
        // (invariant 4.A.14 NF525 prime RGPD). Usage limité aux super_admins en mode démo.
        await supabase.from('customers').delete().eq('tenant_id', profile.tenant_id);
        queryClient.invalidateQueries({ queryKey: ['customers'] });
      } else {
        await supabase.from('fournisseurs').delete().eq('tenant_id', profile.tenant_id);
        queryClient.invalidateQueries({ queryKey: ['fournisseurs'] });
      }
      toast.success(`${label} supprimés avec succès`);
    } catch (err: unknown) {
      toast.error(`Erreur : ${err instanceof Error ? err.message : 'inconnue'}`);
    } finally {
      setDeploying(null);
    }
  };

  useOpenCurrentSection(touch, visibleMenuItems, setOpenSections);

  const toggleSection = (title: string) => {
    setOpenSections((prev) => {
      if (prev.includes(title)) return prev.filter((s) => s !== title);
      return touch ? [title] : [...prev, title];
    });
  };

  const toggleSubSection = (title: string) => {
    setOpenSubSections((prev) =>
      prev.includes(title) ? prev.filter((s) => s !== title) : [...prev, title],
    );
  };

  const isChildActive = (item: MenuItem) => {
    if (item.children?.some((c) => location.pathname === c.url)) return true;
    if (item.sections?.some((s) => s.children.some((c) => location.pathname === c.url)))
      return true;
    return false;
  };

  const renderLink = (child: SubItem, indent = false) => {
    const isActive = location.pathname === child.url;
    return (
      <NavLink
        key={child.url}
        to={child.url}
        end
        className={cn(
          'mx-2 block rounded-md px-3 py-2 text-[13px] transition-all',
          indent ? 'ml-8' : 'ml-6',
          'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
          isActive
            ? 'bg-sidebar-accent font-medium text-sidebar-primary'
            : 'text-sidebar-foreground/90',
        )}
        activeClassName=""
      >
        {child.title}
      </NavLink>
    );
  };

  return (
    <>
      <aside
        data-tour="sidebar"
        className={cn(
          touch && 'vp-menu-tactile',
          'sticky top-0 z-50 flex h-screen flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground transition-all duration-300',
          collapsed ? 'w-[68px]' : 'w-[270px]',
        )}
      >
        {/* Logo */}
        <div
          className={cn(
            'flex items-center border-b border-sidebar-border px-4',
            collapsed ? 'justify-center py-4' : 'justify-between py-4',
          )}
        >
          {collapsed ? (
            <Link to="/dashboard" onClick={handleLogoTap}>
              <img
                src={posVisionIcon}
                alt="VisionPos 360"
                className={cn(
                  'h-auto w-8 cursor-pointer drop-shadow-lg',
                  updateChecking && 'animate-pulse',
                )}
              />
            </Link>
          ) : (
            <Link
              to="/dashboard"
              onClick={handleLogoTap}
              className="flex animate-slide-in items-center gap-3"
            >
              <img
                src={posVisionLogo}
                alt="VisionPos 360"
                className={cn(
                  'h-9 w-auto cursor-pointer drop-shadow-lg',
                  updateChecking && 'animate-pulse',
                )}
              />
            </Link>
          )}
        </div>

        {/* Universe selector — shown only when multiple universes exist */}
        {universes.length > 1 && !collapsed && (
          <div className="border-b border-sidebar-border px-3 py-2">
            <p className="mb-1.5 px-1 text-[10px] uppercase tracking-wider text-sidebar-foreground/50">
              Univers
            </p>
            <div className="flex flex-wrap gap-1">
              <button
                onClick={() => setActiveUniverse(null)}
                className={cn(
                  'rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
                  !activeUniverse
                    ? 'bg-sidebar-primary text-white'
                    : 'bg-sidebar-accent text-sidebar-foreground/80 hover:bg-sidebar-accent/80',
                )}
              >
                Tous
              </button>
              {universes.map((u) => (
                <button
                  key={u.id}
                  onClick={() => setActiveUniverse(u)}
                  className={cn(
                    'rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
                    activeUniverse?.id === u.id
                      ? 'text-white'
                      : 'bg-sidebar-accent text-sidebar-foreground/80 hover:bg-sidebar-accent/80',
                  )}
                  style={
                    activeUniverse?.id === u.id
                      ? { backgroundColor: u.color || '#3b82f6' }
                      : undefined
                  }
                >
                  {u.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-3">
          {touch && <TouchStockShortcut />}
          {visibleMenuItems.map((menuItem) => {
            // Simple link (Accueil, Tutoriels)
            if (menuItem.url && !menuItem.children && !menuItem.sections) {
              const isActive = location.pathname === menuItem.url;
              // Accueil (/dashboard) en mode kiosque : sans bypass de session,
              // useKioskAutoRedirect renverrait instantanément vers la cible kiosque.
              const handleClick =
                menuItem.url === '/dashboard' ? () => bypassKioskThisSession() : undefined;
              return (
                <NavLink
                  key={menuItem.title}
                  to={menuItem.url}
                  end
                  onClick={handleClick}
                  className={cn(
                    'mx-2 flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all',
                    'hover:bg-sidebar-accent',
                    isActive
                      ? 'bg-sidebar-accent text-sidebar-primary'
                      : 'text-sidebar-foreground/90',
                  )}
                  activeClassName=""
                >
                  <menuItem.icon
                    className={cn(
                      'h-[18px] w-[18px] shrink-0',
                      isActive ? 'text-sidebar-primary' : 'text-sidebar-foreground/70',
                    )}
                  />
                  {!collapsed && <span>{menuItem.title}</span>}
                </NavLink>
              );
            }

            // Section with children (rubrique Certification : verrouillée par code)
            if (menuItem.children && !collapsed) {
              const locked = menuItem.codeLocked === true && !certUnlocked && !bypassPin;
              return (
                <SidebarChildrenSection
                  key={menuItem.title}
                  item={{ ...menuItem, children: menuItem.children }}
                  isOpen={openSections.includes(menuItem.title)}
                  hasActive={isChildActive(menuItem)}
                  locked={locked}
                  onToggle={() =>
                    locked ? setShowCertDialog(true) : toggleSection(menuItem.title)
                  }
                  renderLink={(child) => renderLink(child)}
                />
              );
            }

            // Configuration with nested sections
            if (menuItem.sections && !collapsed) {
              const isOpen = openSections.includes(menuItem.title);
              const hasActive = isChildActive(menuItem);
              const isPinGated = menuItem.requiresPin === true;
              const isLocked = isPinGated && !configAccessible;

              return (
                <div key={menuItem.title}>
                  <button
                    onClick={() => {
                      if (isPinGated) {
                        handleConfigClick(menuItem.title);
                      } else {
                        toggleSection(menuItem.title);
                      }
                    }}
                    onMouseDown={isPinGated ? handleConfigLongPressStart : undefined}
                    onMouseUp={isPinGated ? handleConfigLongPressEnd : undefined}
                    onMouseLeave={isPinGated ? handleConfigLongPressEnd : undefined}
                    onTouchStart={isPinGated ? handleConfigLongPressStart : undefined}
                    onTouchEnd={isPinGated ? handleConfigLongPressEnd : undefined}
                    onTouchCancel={isPinGated ? handleConfigLongPressEnd : undefined}
                    title={isLocked ? 'Appui long pour déverrouiller' : undefined}
                    className={cn(
                      'mx-2 flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-sm font-semibold transition-all',
                      'hover:bg-sidebar-accent',
                      hasActive ? 'text-sidebar-primary' : 'text-sidebar-foreground/90',
                    )}
                    style={{ width: 'calc(100% - 16px)' }}
                  >
                    <div className="flex items-center gap-3">
                      <menuItem.icon
                        className={cn(
                          'h-[18px] w-[18px] shrink-0',
                          hasActive ? 'text-sidebar-primary' : 'text-sidebar-foreground/70',
                        )}
                      />
                      <span className="flex items-center gap-1.5">
                        {menuItem.title}
                        {isPinGated &&
                          (isLocked ? (
                            <Lock className="h-3 w-3 text-amber-500/70" />
                          ) : (
                            <Unlock className="h-3 w-3 text-green-500/70" />
                          ))}
                      </span>
                    </div>
                    {isOpen ? (
                      <ChevronUp className="h-3.5 w-3.5 text-sidebar-foreground/40" />
                    ) : (
                      <ChevronDown className="h-3.5 w-3.5 text-sidebar-foreground/40" />
                    )}
                  </button>
                  {isOpen && !isLocked && (
                    <div className="mt-0.5 pb-1">
                      {menuItem.sections.map((section) => {
                        const subOpen = openSubSections.includes(section.title);
                        return (
                          <div key={section.title}>
                            <button
                              onClick={() => toggleSubSection(section.title)}
                              className="ml-8 flex w-full items-center justify-between py-2 pr-5 text-[13px] font-medium text-sidebar-foreground/80 transition-all hover:text-sidebar-foreground"
                              style={{ width: 'calc(100% - 32px)' }}
                            >
                              <span>{section.title}</span>
                              {subOpen ? (
                                <ChevronUp className="h-3 w-3 text-sidebar-foreground/40" />
                              ) : (
                                <ChevronDown className="h-3 w-3 text-sidebar-foreground/40" />
                              )}
                            </button>
                            {subOpen && (
                              <div className="pb-1">
                                {section.children.map((child) => renderLink(child, true))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            // Collapsed mode - just icon
            if (collapsed) {
              const url =
                menuItem.url ||
                (menuItem.children?.[0]?.url ?? menuItem.sections?.[0]?.children[0]?.url ?? '/');
              const isActive = menuItem.url
                ? location.pathname === menuItem.url
                : isChildActive(menuItem);

              return (
                <NavLink
                  key={menuItem.title}
                  to={url}
                  end={menuItem.url === '/'}
                  className={cn(
                    'mx-2 flex items-center justify-center rounded-lg py-2.5 transition-all',
                    'hover:bg-sidebar-accent',
                    isActive
                      ? 'bg-sidebar-accent text-sidebar-primary'
                      : 'text-sidebar-foreground/80',
                  )}
                  activeClassName=""
                  title={menuItem.title}
                >
                  <menuItem.icon className="h-[18px] w-[18px]" />
                </NavLink>
              );
            }

            return null;
          })}

          {/* Tenant Demo Panel — visible only for LinkVision super_admin impersonating */}
          {isSuperAdmin && isLinkVision && isImpersonating && !collapsed && (
            <div className="mx-2 mb-1 mt-3">
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3">
                <button
                  onClick={() => setDemoOpen((prev) => !prev)}
                  className="flex w-full items-center justify-between text-sm font-semibold text-amber-600"
                >
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    <span className="max-w-[160px] truncate">{impersonatedName}</span>
                  </div>
                  {demoOpen ? (
                    <ChevronUp className="h-3.5 w-3.5" />
                  ) : (
                    <ChevronDown className="h-3.5 w-3.5" />
                  )}
                </button>

                {demoOpen && (
                  <div className="mt-2 space-y-1">
                    <p className="mb-1.5 px-1 text-[11px] font-medium uppercase tracking-wider text-sidebar-foreground/50">
                      Injecter un catalogue
                    </p>
                    {DEMO_SECTORS.map((s) => (
                      <button
                        key={s.key}
                        disabled={!!deploying}
                        onClick={() => deployDemoCatalog(s.key)}
                        className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-[13px] text-sidebar-foreground/80 transition-colors hover:bg-sidebar-accent disabled:opacity-50"
                      >
                        {deploying === s.key ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Database className="h-3.5 w-3.5 text-sidebar-foreground/50" />
                        )}
                        {s.label}
                      </button>
                    ))}

                    <div className="my-2 border-t border-sidebar-border" />

                    <button
                      disabled={!!deploying}
                      onClick={deployDemoCustomers}
                      className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-[13px] text-sidebar-foreground/80 transition-colors hover:bg-sidebar-accent disabled:opacity-50"
                    >
                      {deploying === 'customers' ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Users className="h-3.5 w-3.5 text-sidebar-foreground/50" />
                      )}
                      Clients de démonstration
                    </button>

                    <div className="my-2 border-t border-sidebar-border" />

                    <p className="mb-1.5 px-1 text-[11px] font-medium uppercase tracking-wider text-destructive/70">
                      Supprimer les données
                    </p>
                    <button
                      disabled={!!deploying}
                      onClick={() => purgeDemoData('products')}
                      className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-[13px] text-destructive/80 transition-colors hover:bg-destructive/10 disabled:opacity-50"
                    >
                      {deploying === 'purge-products' ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="h-3.5 w-3.5" />
                      )}
                      Produits & catégories
                    </button>
                    <button
                      disabled={!!deploying}
                      onClick={() => purgeDemoData('customers')}
                      className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-[13px] text-destructive/80 transition-colors hover:bg-destructive/10 disabled:opacity-50"
                    >
                      {deploying === 'purge-customers' ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="h-3.5 w-3.5" />
                      )}
                      Clients
                    </button>
                    <button
                      disabled={!!deploying}
                      onClick={() => purgeDemoData('fournisseurs')}
                      className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-[13px] text-destructive/80 transition-colors hover:bg-destructive/10 disabled:opacity-50"
                    >
                      {deploying === 'purge-fournisseurs' ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="h-3.5 w-3.5" />
                      )}
                      Fournisseurs
                    </button>

                    <div className="my-2 border-t border-sidebar-border" />

                    <p className="mb-1.5 px-1 text-[11px] font-medium uppercase tracking-wider text-sidebar-foreground/50">
                      Connexion matériel
                    </p>

                    <button
                      onClick={() => {
                        toast.info('Recherche de TPE Bluetooth…');
                        (navigator as any).bluetooth
                          ?.requestDevice?.({
                            acceptAllDevices: true,
                            optionalServices: ['generic_access'],
                          })
                          .then((d) => toast.success(`TPE détecté : ${d.name || d.id}`))
                          .catch(() => toast.error('Aucun TPE Bluetooth trouvé'));
                      }}
                      className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-[13px] text-sidebar-foreground/80 transition-colors hover:bg-sidebar-accent"
                    >
                      <Bluetooth className="h-3.5 w-3.5 text-sidebar-foreground/50" />
                      TPE Bluetooth
                    </button>

                    <button
                      onClick={async () => {
                        toast.info('Recherche de TPE USB…');
                        try {
                          const d = await (navigator as any).usb?.requestDevice?.({ filters: [] });
                          toast.success(
                            `TPE USB détecté : ${d?.productName || d?.serialNumber || 'Appareil'}`,
                          );
                        } catch {
                          toast.error('Aucun TPE USB trouvé');
                        }
                      }}
                      className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-[13px] text-sidebar-foreground/80 transition-colors hover:bg-sidebar-accent"
                    >
                      <Usb className="h-3.5 w-3.5 text-sidebar-foreground/50" />
                      TPE USB
                    </button>

                    <Link
                      to="/togo/imprimantes"
                      className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-[13px] text-sidebar-foreground/80 transition-colors hover:bg-sidebar-accent"
                    >
                      <Printer className="h-3.5 w-3.5 text-sidebar-foreground/50" />
                      Gérer les imprimantes
                    </Link>

                    <Link
                      to="/togo/materiel"
                      className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-[13px] text-sidebar-foreground/80 transition-colors hover:bg-sidebar-accent"
                    >
                      <Monitor className="h-3.5 w-3.5 text-sidebar-foreground/50" />
                      Matériel connecté
                    </Link>

                    <Link
                      to="/togo/tpe"
                      className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-[13px] text-sidebar-foreground/80 transition-colors hover:bg-sidebar-accent"
                    >
                      <Wifi className="h-3.5 w-3.5 text-sidebar-foreground/50" />
                      Configurer TPE
                    </Link>

                    <Link
                      to="/togo/tickets"
                      className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-[13px] text-sidebar-foreground/80 transition-colors hover:bg-sidebar-accent"
                    >
                      <ScanBarcode className="h-3.5 w-3.5 text-sidebar-foreground/50" />
                      Personnaliser tickets
                    </Link>

                    <button
                      onClick={async () => {
                        toast.info('Recherche de tablette USB…');
                        try {
                          const d = await (navigator as any).usb?.requestDevice?.({ filters: [] });
                          toast.success(
                            `Tablette détectée : ${d?.productName || d?.serialNumber || 'Appareil'}`,
                          );
                        } catch {
                          toast.error('Aucune tablette USB trouvée');
                        }
                      }}
                      className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-[13px] text-sidebar-foreground/80 transition-colors hover:bg-sidebar-accent"
                    >
                      <Tablet className="h-3.5 w-3.5 text-sidebar-foreground/50" />
                      Tablette
                    </button>

                    <button
                      onClick={() =>
                        toast.info(
                          "Configurez l'écran cuisine via Configuration > Postes de travail (adresse IP)",
                        )
                      }
                      className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-[13px] text-sidebar-foreground/80 transition-colors hover:bg-sidebar-accent"
                    >
                      <MonitorPlay className="h-3.5 w-3.5 text-sidebar-foreground/50" />
                      Écran de cuisine (KDS)
                    </button>

                    <button
                      onClick={() =>
                        toast.info(
                          "Configurez l'écran client via Configuration > Postes de travail",
                        )
                      }
                      className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-[13px] text-sidebar-foreground/80 transition-colors hover:bg-sidebar-accent"
                    >
                      <Monitor className="h-3.5 w-3.5 text-sidebar-foreground/50" />
                      Écran client
                    </button>

                    <button
                      onClick={() =>
                        toast.info(
                          'Configurez la borne tactile via Configuration > Postes de travail',
                        )
                      }
                      className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-[13px] text-sidebar-foreground/80 transition-colors hover:bg-sidebar-accent"
                    >
                      <TouchpadIcon className="h-3.5 w-3.5 text-sidebar-foreground/50" />
                      Borne tactile
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </nav>

        <SidebarFooter
          touch={touch}
          collapsed={collapsed}
          onToggle={() => setCollapsed(!collapsed)}
          menu={visibleMenuItems.filter(
            (i) =>
              !(i.requiresPin && !configAccessible) &&
              !(i.codeLocked && !certUnlocked && !bypassPin),
          )}
        />
      </aside>

      <NetworkPrinterDialog
        open={showNetworkPrinter}
        onOpenChange={setShowNetworkPrinter}
        impersonatedUserId={impersonatedUserId}
      />
      <CertificationCodeDialog
        open={showCertDialog}
        onOpenChange={setShowCertDialog}
        onUnlock={() => {
          setCertUnlocked(true);
          setOpenSections((prev) =>
            prev.includes('Certification') ? prev : [...prev, 'Certification'],
          );
        }}
      />
      <ConfigPinDialog
        open={showPinDialog}
        onOpenChange={setShowPinDialog}
        onUnlock={() => setConfigUnlocked(true)}
      />
    </>
  );
}
